import { JetView } from 'webix-jet'; // Importing JetView from webix-jet
import { devConfig, profile } from 'config/config'; // Importing configuration settings

/**
 * FilterIncomesView class extends JetView to create a view for filtering income records.
 */
export default class FilterIncomesView extends JetView {
    /**
     * Configures the layout of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const screen = this.app.config.size; // Screen size configuration

        // Left section with input for consecutive number
        const left = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'text',
                    name: 'Consecutive',
                    type: 'text',
                    label: "<span class='icon-drivers-license-o'></span>" + _('Consecutivo'), // Label for consecutive input
                    labelPosition: 'top',
                    placeholder: _('Consecutivo'), // Placeholder text
                },
            ],
        };

        // Center left section with date picker and date input
        const center_left = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'text',
                    name: 'Datepicker',
                    id: 'Datepicker',
                    css: 'reportrange',
                    readonly: true,
                    label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de Creación'), // Label for date picker
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Placeholder text
                },
                {
                    view: 'datepicker',
                    name: 'Date',
                    id: 'Date',
                    stringResult: true,
                    minWidth: 200,
                    label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for date input
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Placeholder text
                    value: moment().format('YYYY-MM-DD'), // Default date value
                    format: '%d %M %Y', // Date format
                    suggest: {
                        type: 'calendar',
                        body: {
                            maxDate: new Date(), // Maximum selectable date
                        },
                    },
                },
            ],
        };

        // Center right section with radio buttons for credit card option
        const center_right = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'radio',
                    name: 'CreditCards',
                    label: _('Tarjeta de crédito'), // Label for credit card option
                    labelPosition: 'top',
                    value: 0,
                    options: [
                        { id: 0, value: _('Sí') }, // Yes option
                        { id: 1, value: _('No') }, // No option
                    ],
                },
            ],
        };

        // Right section (currently empty)
        const right = {
            margin: 5,
            minWidth: 200,
            rows: [],
        };

        // Additional sections for filtering options
        const left2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'combo',
                    name: 'TypeIncomes',
                    id: 'TypeIncomesFilter',
                    label: _('Tipo de Egreso'), // Label for income type selection
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Placeholder text
                },
            ],
        };

        const center_left2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'combo',
                    name: 'Concepts',
                    id: 'ConceptsFilter',
                    label: _('Concepto'), // Label for concept selection
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Placeholder text
                },
            ],
        };

        const center_right2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'text',
                    name: 'DocumentID',
                    type: 'number',
                    label: _('Documento ID'), // Label for document ID input
                    labelPosition: 'top',
                    placeholder: _('Documento ID'), // Placeholder text
                },
            ],
        };

        const right2 = {
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'richselect',
                    name: 'IsActivate',
                    label: "<span class='icon-state'></span>" + _('Estado'), // Label for activation state selection
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Placeholder text
                    options: [
                        { id: 'null', value: _('Todos') }, // All option
                        { id: 'A', value: _('Activado') }, // Activated option
                        { id: 'I', value: _('Inactivado') }, // Inactivated option
                    ],
                },
            ],
        };

        // Upper section layout
        const upper_section = {
            id: 'a1',
            rows: [
                {
                    responsive: 'a1',
                    margin: 10,
                    cols: [left, center_left, center_right, right],
                },
            ],
        };

        // Second upper section layout
        const upper_section2 = {
            id: 'a1',
            rows: [
                {
                    responsive: 'a1',
                    margin: 10,
                    cols: [left2, center_left2, center_right2, right2],
                },
            ],
        };

        // Accordion section for advanced filters
        const accordion_section = {
            id: 'b5',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: true,
                    paddingY: 10,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros Avanzados'), // Header for advanced filters
                            body: {
                                padding: 10,
                                rows: [upper_section2],
                            },
                        },
                    ],
                },
            ],
        };

        // Buttons for actions
        const buttons = {
            margin: 5,
            cols: [
                {},
                {
                    view: 'button',
                    value: _('Añadir Ingreso'), // Button to add income
                    type: 'form',
                    autowidth: true,
                    hidden: !devConfig.getPermAction(
                        'betShopReportsIncomesAdd',
                        'Add', // Check permission for adding income
                    ),
                    css: 'prm-betShopReportsIncomesAdd',
                    click: () => {
                        this.app.callEvent('AddIncome:button', [{}]); // Trigger add income event
                    },
                },
                {
                    view: 'button',
                    value: _('Limpiar'), // Button to clear inputs
                    autowidth: true,
                    click: () => {
                        this.getRoot().clear(); // Clear the form inputs
                    },
                },
                {
                    view: 'button',
                    value: _('Consultar'), // Button to consult/filter
                    hotkey: 'enter',
                    type: 'form',
                    autowidth: true,
                    click: () => {
                        const filterdata = this.getRoot().getValues(); // Get form values
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date']; // Remove date if typeDate is not 2
                        } else {
                            if (true) {
                                delete filterdata['Datepicker']; // Remove datepicker if condition is true
                            }
                        }
                        this.app.callEvent('incomes:filter', [filterdata]); // Trigger filter event
                        $$('b5').collapse(); // Collapse the accordion
                    },
                },
            ],
        };

        // Accordion section for main filters
        const accordion_section2 = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: false,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Header for filters
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [
                                    upper_section,
                                    accordion_section,
                                    buttons, // Include buttons in the accordion
                                ],
                            },
                        },
                    ],
                },
            ],
        };

        // Return the main configuration for the form
        return {
            view: 'form',
            type: 'clean',
            rows: [accordion_section2],
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     */
    init() {
        const _ = this.app.getService('locale')._; // Localization service
        webix.ready(function () {
            if (profile.typeDate !== 2) {
                $$('Date').hide(); // Hide date input if typeDate is not 2
                $$('Datepicker').show(); // Show datepicker
            } else {
                if (true) {
                    $$('Date').show(); // Show date input if condition is true
                    $$('Datepicker').hide(); // Hide datepicker
                }
            }
        });

        const lang = this.app.getService('locale').getLang(); // Get current language
        $$('Datepicker').attachEvent('onAfterRender', function () {
            devConfig.setDateModal(lang); // Set date modal based on language
        });

        // AJAX request to get types for income filter
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json', // Set content type for request
            })
            .post(
                devConfig.geturlApi() + 'Accounting/GetTypesCenterPositionSelect',
                '{"Type":3}', // Request payload
                {
                    error: function (text, data, XmlHttpRequest) {
                        webix.message(_('Error! ')); // Show error message on failure
                    },
                    success: function (text, data, XmlHttpRequest) {
                        var resp = JSON.parse(text); // Parse response
                        $$('TypeIncomesFilter').define('options', resp.Data); // Set options for income type filter
                        $$('TypeIncomesFilter').refresh(); // Refresh the filter
                    },
                },
            );

        // AJAX request to get concepts for filter
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json', // Set content type for request
            })
            .post(devConfig.geturlApi() + 'Accounting/GetConceptsSelect', '', {
                error: function (text, data, XmlHttpRequest) {
                    webix.message(_('Error! ')); // Show error message on failure
                },
                success: function (text, data, XmlHttpRequest) {
                    var resp = JSON.parse(text); // Parse response
                    $$('ConceptsFilter').define('options', resp.Data); // Set options for concepts filter
                    $$('ConceptsFilter').refresh(); // Refresh the filter
                },
            });
    }
}

// FILE DOCUMENTED
